// RegisterAccount Component - Admin Account Creation Step
// Full-viewport-height card under stepper, edge-to-edge on small screens; content scales (grid, compact type).
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Lock, Eye, EyeOff, AlertCircle, ChevronRight, Building2, Phone } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { QAWAEM_LOGO_FULL } from '../../../products/saudi/config/qawaemAssets';
import { SAUDI_BRAND } from '../../../products/saudi/config/saudiBrandConfig';

/** Aligné avec `public/index.html` et `SetupWizard`. */
const QAWAEM_FAVICON_SRC = encodeURI(
  `${process.env.PUBLIC_URL ?? ''}/qawaem/favicon.png`
);

const SETUP_LABEL_CLASS = 'mb-1.5 block text-xs font-medium text-white/70 sm:mb-2 sm:text-sm';

const SETUP_INPUT_CLASS =
  'w-full rounded-xl border border-white/20 bg-white/5 py-2.5 pl-10 pr-4 text-sm text-white placeholder:text-white/35 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 sm:py-3 sm:pl-12';

const SETUP_INPUT_PASSWORD_CLASS =
  'w-full rounded-xl border border-white/20 bg-white/5 py-2.5 pl-10 pr-10 text-sm text-white placeholder:text-white/35 focus:border-blue-400/50 focus:outline-none focus:ring-2 focus:ring-blue-500/30 sm:py-3 sm:pl-12 sm:pr-12';

const SETUP_ERROR_CLASS = 'mt-1.5 flex items-center gap-1 text-xs text-red-300 sm:mt-2 sm:text-sm';

/** Same icon placement as WelcomeLoginPage / ProductKey setup steps. */
function FieldWrap({ children, icon: Icon }) {
  return (
    <div className="relative">
      <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35 sm:left-4">
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
      </span>
      {children}
    </div>
  );
}

const RegisterAccount = ({ data, updateData, nextStep, prevStep, currentStep, totalSteps, variant = 'qawaem' }) => {
  const isQawaem = variant === 'qawaem';
  const labelClass = isQawaem ? 'mb-1.5 block text-xs font-medium text-console-muted sm:mb-2 sm:text-sm' : SETUP_LABEL_CLASS;
  const inputClass = isQawaem
    ? 'w-full glass-input py-2.5 ps-3 pe-10 text-sm sm:py-3 sm:pe-12'
    : SETUP_INPUT_CLASS;
  const inputPasswordClass = isQawaem
    ? 'w-full glass-input py-2.5 ps-3 pe-16 text-sm sm:py-3'
    : SETUP_INPUT_PASSWORD_CLASS;
  const errorClass = isQawaem
    ? 'mt-1.5 flex items-center gap-1 text-xs text-red-600 sm:mt-2 sm:text-sm'
    : SETUP_ERROR_CLASS;
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  const handleChange = (field, value) => {
    const fieldPath = field;
    updateData(fieldPath, value);
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!data.adminUsername || data.adminUsername.trim().length < 3) {
      newErrors.adminUsername = t('setup.usernameMinLength');
    }

    if (!data.adminEmail || !/\S+@\S+\.\S+/.test(data.adminEmail)) {
      newErrors.adminEmail = t('setup.invalidEmail');
    }

    if (!data.companyEntityName || data.companyEntityName.trim().length < 2) {
      newErrors.companyEntityName = t('setup.entityNameRequired');
    }

    if (!data.companyPhone || data.companyPhone.trim().length < 8) {
      newErrors.companyPhone = t('setup.phoneRequired');
    }

    if (!data.adminPassword || data.adminPassword.length < 6) {
      newErrors.adminPassword = t('setup.passwordMinLength');
    }

    if (!data.adminPasswordConfirm || !data.adminPasswordConfirm.trim()) {
      newErrors.adminPasswordConfirm = t('setup.confirmPasswordRequired');
    } else if (data.adminPassword !== data.adminPasswordConfirm) {
      newErrors.adminPasswordConfirm = t('setup.passwordsMismatch');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      nextStep();
    }
  };

  return (
    <div
      className={isQawaem
        ? 'glass-modal relative flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden'
        : 'relative flex h-full min-h-0 w-full min-w-0 max-w-none flex-1 flex-col overflow-hidden rounded-none border-x-0 border-y border-white/15 bg-white/10 shadow-xl shadow-black/20 backdrop-blur-lg sm:rounded-3xl sm:border sm:border-white/20 sm:shadow-black/25'}
    >
      {!isQawaem ? (
        <div
          className="pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full bg-blue-500/15 blur-3xl"
          aria-hidden
        />
      ) : null}

      <div className="relative flex min-h-0 flex-1 flex-col px-3 py-2 sm:px-4 sm:py-3 md:px-5 md:py-4 lg:px-6 xl:px-8 2xl:px-10">
        <div className="shrink-0 text-center">
          <motion.img
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            src={isQawaem ? QAWAEM_LOGO_FULL : QAWAEM_FAVICON_SRC}
            alt={isQawaem ? SAUDI_BRAND.appName : 'Qawaem'}
            className={isQawaem
              ? 'mx-auto mb-2 h-12 w-auto object-contain sm:mb-3 sm:h-14'
              : 'mx-auto mb-1 h-9 w-9 object-contain drop-shadow-[0_8px_24px_rgb(15_23_42/0.55)] sm:mb-1.5 sm:h-11 sm:w-11 md:h-12 md:w-12 lg:mb-2 lg:h-14 lg:w-14'}
            draggable={false}
          />
          <h1 className={isQawaem
            ? 'text-[clamp(1rem,4vw,1.75rem)] font-bold leading-tight text-console md:text-2xl lg:text-3xl'
            : 'text-[clamp(1rem,4vw,1.75rem)] font-bold leading-tight text-white md:text-2xl lg:text-3xl'}>
            {isQawaem ? 'إنشاء حساب المسؤول' : t('setup.createAdminAccount')}
          </h1>
          <p className={isQawaem ? 'mt-1 text-sm text-console-muted sm:mt-1.5 sm:text-base' : 'mt-1 text-sm text-white/60 sm:mt-1.5 sm:text-base'}>
            {isQawaem ? 'أدخل بيانات المؤسسة وحساب المسؤول الرئيسي.' : t('setup.adminAccountDescription')}
          </p>
        </div>

        <div className="flex min-h-0 w-full min-w-0 flex-1 flex-col justify-center py-2 sm:py-3 md:py-4">
          <div className="grid w-full min-w-0 grid-cols-1 gap-x-6 gap-y-4 md:grid-cols-2 md:gap-x-8 md:gap-y-5">
            <div className="min-w-0">
              <label htmlFor="adminUsername" className={labelClass}>
                {t('setup.username')} <span className="text-red-400">*</span>
              </label>
              <FieldWrap icon={User}>
                <input
                  id="adminUsername"
                  type="text"
                  required
                  minLength={3}
                  className={inputClass}
                  value={data.adminUsername || ''}
                  onChange={(e) => handleChange('adminUsername', e.target.value)}
                  placeholder={t('setup.usernamePlaceholder')}
                />
              </FieldWrap>
              {errors.adminUsername && (
                <p className={errorClass}>
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.adminUsername}
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label htmlFor="adminEmail" className={labelClass}>
                {t('setup.email')} <span className="text-red-400">*</span>
              </label>
              <FieldWrap icon={Mail}>
                <input
                  id="adminEmail"
                  type="email"
                  required
                  className={inputClass}
                  value={data.adminEmail || ''}
                  onChange={(e) => handleChange('adminEmail', e.target.value)}
                  placeholder={t('setup.emailPlaceholder')}
                />
              </FieldWrap>
              {errors.adminEmail && (
                <p className={errorClass}>
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.adminEmail}
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label htmlFor="companyEntityName" className={labelClass}>
                {t('setup.entityName')} <span className="text-red-400">*</span>
              </label>
              <FieldWrap icon={Building2}>
                <input
                  id="companyEntityName"
                  type="text"
                  required
                  minLength={2}
                  className={inputClass}
                  value={data.companyEntityName || ''}
                  onChange={(e) => handleChange('companyEntityName', e.target.value)}
                  placeholder={t('setup.entityNamePlaceholder')}
                />
              </FieldWrap>
              {errors.companyEntityName && (
                <p className={errorClass}>
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.companyEntityName}
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label htmlFor="companyPhone" className={labelClass}>
                {t('setup.companyPhone')} <span className="text-red-400">*</span>
              </label>
              <FieldWrap icon={Phone}>
                <input
                  id="companyPhone"
                  type="tel"
                  required
                  minLength={8}
                  className={inputClass}
                  value={data.companyPhone || ''}
                  onChange={(e) => handleChange('companyPhone', e.target.value)}
                  placeholder={t('setup.companyPhonePlaceholder')}
                />
              </FieldWrap>
              {errors.companyPhone && (
                <p className={errorClass}>
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.companyPhone}
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label htmlFor="adminPassword" className={labelClass}>
                {t('setup.password')} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35 sm:left-4">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                </span>
                <input
                  id="adminPassword"
                  type={showPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  className={inputPasswordClass}
                  value={data.adminPassword || ''}
                  onChange={(e) => handleChange('adminPassword', e.target.value)}
                  placeholder={t('setup.passwordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-white/45 transition-colors hover:text-white sm:right-4"
                  aria-label={showPassword ? t('setup.hidePassword') : t('setup.showPassword')}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                  )}
                </button>
              </div>
              {errors.adminPassword && (
                <p className={errorClass}>
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.adminPassword}
                </p>
              )}
            </div>

            <div className="min-w-0">
              <label htmlFor="adminPasswordConfirm" className={labelClass}>
                {t('setup.confirmPassword')} <span className="text-red-400">*</span>
              </label>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-white/35 sm:left-4">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                </span>
                <input
                  id="adminPasswordConfirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  required
                  minLength={6}
                  className={inputPasswordClass}
                  value={data.adminPasswordConfirm || ''}
                  onChange={(e) => handleChange('adminPasswordConfirm', e.target.value)}
                  placeholder={t('setup.confirmPasswordPlaceholder')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-white/45 transition-colors hover:text-white sm:right-4"
                  aria-label={showConfirmPassword ? t('setup.hideConfirmPassword') : t('setup.showConfirmPassword')}
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5" aria-hidden />
                  )}
                </button>
              </div>
              {errors.adminPasswordConfirm && (
                <p className={errorClass}>
                  <AlertCircle className="h-4 w-4 shrink-0" aria-hidden />
                  {errors.adminPasswordConfirm}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className={`mt-auto flex w-full min-w-0 shrink-0 flex-col space-y-1.5 border-t pt-2 sm:space-y-2 sm:pt-3 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3 md:space-y-0 md:pt-4 ${isQawaem ? 'border-[#c5d6c5]/40' : 'border-white/10'}`}>
          <button
            type="button"
            onClick={prevStep}
            className={isQawaem
              ? 'w-full rounded-xl border border-[#c5d6c5]/50 bg-white/70 px-4 py-2.5 text-sm font-semibold text-console transition-colors hover:bg-[#f4f7f4] md:w-auto'
              : 'w-full rounded-lg border border-white/20 bg-white/5 px-3 py-1.5 text-[11px] font-semibold text-white/90 transition-colors hover:bg-white/10 sm:px-4 sm:py-2 sm:text-sm md:w-auto md:rounded-xl md:px-5 md:py-2.5'}
          >
            {isQawaem ? 'السابق' : t('setup.previous')}
          </button>
          <motion.button
            type="button"
            onClick={handleSubmit}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={isQawaem
              ? 'flex w-full items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold btn-primary-forest md:w-auto'
              : 'flex w-full items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-700 px-3 py-1.5 text-[11px] font-semibold text-white shadow-lg shadow-blue-500/25 transition-all hover:from-blue-600 hover:to-blue-800 sm:px-4 sm:py-2 sm:text-sm md:w-auto md:rounded-xl md:px-5 md:py-2.5'}
          >
            {isQawaem ? 'إنهاء التثبيت' : t('setup.next')}
            <ChevronRight className="h-3.5 w-3.5 shrink-0 md:h-5 md:w-5" aria-hidden />
          </motion.button>
        </div>
        <p className={`mt-1 shrink-0 pb-1 text-center text-[9px] sm:mt-1.5 sm:text-xs md:pb-0 ${isQawaem ? 'text-console-subtle' : 'text-white/40'}`}>
          {currentStep + 1} / {totalSteps}
        </p>
      </div>
    </div>
  );
};

export default RegisterAccount;
